import { BlockType, CreateBlockType } from "../../types/block/type";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { inboxQueryKeys } from "./queries";
import { useSetRecoilState } from "recoil";
import { mobaCalendarListState } from "../../recoil/calendar/mobaCalendarListState";
import { initialCreatingCalendarBlock } from "./utils/initialCreatingCalendarBlock";
import { useReorderInboxBlockMutationOptions } from "./mutations";
import useFindUpdateInboxQueryKey from "../../hooks/inbox/useFindUpdateInboxQueryKey";
import {
  convertClientToServerBlockType,
  convertServerToClientBlockType,
} from "../../utils/common/block/type-converter";
import { calendarQueryKeys } from "../calendar/queries";
import { debounceInvalidate } from "../../services/optimistic-updates/debounceInvalidate";
import { isAxiosError } from "axios";
import { BlockVisibility } from "../../types/block/enum";
import { usePreference } from "../../hooks/preference/usePreference";
import blockAPI from "../../api/BlockAPI";
export const useDuplicateInboxBlockMutation = () => {
  const queryClient = useQueryClient();
  const inboxGeneralQueryKey = inboxQueryKeys.inboxGeneral();
  const { isSameInboxQueryKey, findUpdateInboxQueryKey } = useFindUpdateInboxQueryKey();
  const useReorderInboxBlock = useMutation(useReorderInboxBlockMutationOptions());

  const setMobaCalendarList = useSetRecoilState(mobaCalendarListState);
  const { preference: userPreference } = usePreference();
  const defaultVisibility = userPreference?.defaultVisibility ?? BlockVisibility.PUBLIC;

  return useMutation({
    mutationFn: ({
      payload,
      originalBlock,
    }: {
      payload: CreateBlockType;
      originalBlock: BlockType; // 낙관적 업데이트 위해서 받도록 함
    }) => {
      const serverPayload = convertClientToServerBlockType({
        clientBlock: payload,
        isUpdate: false,
      });
      return blockAPI.createBlock(serverPayload).then((res) => res.data);
    },

    onMutate: async ({ payload, originalBlock }) => {
      const creatingCalendarBlock = {
        ...initialCreatingCalendarBlock,
        ...payload,
      };

      // 캘린더 영역 전역상태 업데이트
      if (creatingCalendarBlock.start && creatingCalendarBlock.end) {
        setMobaCalendarList((prev) => [...prev, creatingCalendarBlock]);
      }

      // 쿼리 캐시 업데이트
      const currentQueryKey = findUpdateInboxQueryKey(creatingCalendarBlock);
      if (!currentQueryKey) return;

      await queryClient.cancelQueries({ queryKey: currentQueryKey });
      const previousData = queryClient.getQueryData<BlockType[]>(currentQueryKey);
      if (previousData) {
        queryClient.setQueryData(currentQueryKey, (oldData: BlockType[]) => {
          const index = oldData.findIndex((block) => block.id === originalBlock.id);
          let newDataArray;
          if (index === -1) {
            newDataArray = [creatingCalendarBlock, ...oldData];
          } else {
            newDataArray = [...oldData];
            newDataArray.splice(index + 1, 0, creatingCalendarBlock);
          }
          return newDataArray;
        });

        return { previousData, currentQueryKey };
      }
    },
    onSuccess: (newData) => {
      const newClientBlock = convertServerToClientBlockType({
        serverBlock: newData,
        defaultVisibility,
      });

      const currentQueryKey = findUpdateInboxQueryKey(newClientBlock);
      if (!currentQueryKey) return;

      // 캘린더 전역상태 업데이트
      if (newClientBlock.start && newClientBlock.end) {
        setMobaCalendarList((prev) => {
          return prev.map((block) =>
            block.clientId === newClientBlock.clientId ? newClientBlock : block
          );
        });
      }

      // inbox에서 임시 생성 블록 제거 및 갱신
      queryClient.setQueryData(currentQueryKey, (oldData: BlockType[] | undefined) => {
        if (!oldData) return [newClientBlock];
        const newDataArray = oldData.map((block) =>
          block.clientId === newClientBlock.clientId ? newClientBlock : block
        );

        // inbox-general인 경우 순서 api 호출
        const isInboxGeneralQueryKey = isSameInboxQueryKey(currentQueryKey, inboxGeneralQueryKey);
        if (isInboxGeneralQueryKey) {
          const blockIds = newDataArray.map((block) => block.id);
          useReorderInboxBlock.mutate({ ids: blockIds });
        }

        return newDataArray;
      });

      // NOTE 해당 부분 확인 필요
      debounceInvalidate.enqueueInvalidate(currentQueryKey);
    },
    onError: (error, variables, context) => {
      if (isAxiosError(error)) {
        if (context?.previousData && context?.currentQueryKey) {
          queryClient.setQueryData(context.currentQueryKey, context.previousData);
        }
        debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
          shouldInvalidateImmediately: true,
        });
      }
    },
  });
};
